import * as styles from "./AlertDialogContent.css";

import { clsx } from "clsx";
import { type JSX, useCallback, useMemo, useRef } from "react";

import { AlertDialog as BaseAlertDialog } from "@base-ui/react/alert-dialog";
import {
  ShortcutScopeContext,
  useShortcutScope,
} from "@frontend/hooks/useScopedShortcut";
import { mergeRefs } from "@frontend/utils/mergeRefs";
import { growFromOrigin } from "@frontend/utils/transitions";

import { useAlertDialogAnimationContext } from "./AlertDialog.context";
import type { AlertDialogContentProps } from "./AlertDialog.types";
import { AlertDialogBackdrop } from "./AlertDialogBackdrop";
import { AlertDialogPortal } from "./AlertDialogPortal";

export function AlertDialogContent({
  className,
  children,
  transition = growFromOrigin,
  ...props
}: AlertDialogContentProps): JSX.Element {
  const originRef = useAlertDialogAnimationContext();

  const observerRef = useRef<MutationObserver | null>(null);

  const openRef = useRef(false);

  const popupCallbackRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) {
        observerRef.current?.disconnect();
        observerRef.current = null;
        return;
      }

      const enter = () => {
        if (openRef.current) {
          return;
        }

        openRef.current = true;
        transition.enter(el, originRef.current);
      };

      const exit = () => {
        if (!openRef.current) {
          return;
        }

        openRef.current = false;

        transition.exit(el, originRef.current);

        originRef.current = null;
      };

      enter();

      const observer = new MutationObserver(() => {
        if (el.hasAttribute("data-closed")) {
          exit();

          return;
        }

        // Reopened without remounting: the exit animation left it invisible.
        enter();
      });

      observer.observe(el, {
        attributes: true,
        attributeFilter: ["data-closed"],
      });

      observerRef.current = observer;
    },
    [originRef, transition],
  );

  const { scope, ref: scopeRef } = useShortcutScope({ modal: true });

  const popupRef = useMemo(
    () => mergeRefs(popupCallbackRef, scopeRef),
    [popupCallbackRef, scopeRef],
  );

  return (
    <AlertDialogPortal>
      <AlertDialogBackdrop />
      <BaseAlertDialog.Popup
        ref={popupRef}
        data-slot="alert-dialog-content"
        className={clsx(styles.popup, className)}
        {...props}
      >
        <ShortcutScopeContext value={scope}>{children}</ShortcutScopeContext>
      </BaseAlertDialog.Popup>
    </AlertDialogPortal>
  );
}
